import type { TFunction } from "i18next";
import {
  MonitoringAlert,
  MonitoringAlertFeed,
  MonitoringListingSummary,
} from "src/Types/alerts.types";
import {
  CommunityBoundary,
  MonitoringAddress,
  MonitoringMode,
} from "src/Types/monitoring.types";
import {
  ReportData,
  ReportListingGroup,
  ReportMetadata,
  ReportStatistics,
} from "src/Types/report.types";
import { AppLocale } from "src/i18n/localesConfig";
import { formatDateTime, formatLongDuration } from "src/i18n/format";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Calculate the duration in days between two dates
 */
const calculateDurationDays = (start: string, end?: string): number => {
  const startDate = new Date(start);
  const endDate = end ? new Date(end) : new Date();
  return Math.max(0, Math.floor((endDate.getTime() - startDate.getTime()) / MS_PER_DAY));
};

/**
 * Check if a listing is new (first seen within the last 30 days)
 */
const isNewListing = (listing: MonitoringListingSummary): boolean => {
  const daysSinceFirst = calculateDurationDays(listing.firstSeenAt);
  return daysSinceFirst <= 30;
};

/**
 * Extract unique listings from alerts
 */
const extractUniqueListings = (alerts: MonitoringAlert[]): MonitoringListingSummary[] => {
  const listingsMap = new Map<string, MonitoringListingSummary>();
  
  alerts.forEach((alert) => {
    if (alert.listing) {
      const key = `${alert.listing.provider}-${alert.listing.providerListingId}`;
      if (!listingsMap.has(key)) {
        listingsMap.set(key, alert.listing);
      }
    }
  });

  return Array.from(listingsMap.values());
};

/**
 * Parse price string to number (e.g., "$150" -> 150)
 */
const parsePrice = (price: string | null): number | null => {
  if (!price) return null;
  const cleaned = price.replace(/[^0-9.]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Calculate report statistics from listings
 */
export const calculateStatistics = (
  listings: MonitoringListingSummary[]
): ReportStatistics => {
  const airbnbListings = listings.filter((l) => l.provider === "AIRBNB");
  const vrboListings = listings.filter((l) => l.provider === "VRBO");
  const activeListings = listings.filter((l) => l.active);
  const inactiveListings = listings.filter((l) => !l.active);
  const newListings = listings.filter(isNewListing);
  const insideBoundary = listings.filter((l) => l.matchStatus === "INSIDE");
  const nearbyBoundary = listings.filter((l) => l.matchStatus === "NEARBY");

  // Calculate average listing duration
  let totalDurationDays = 0;
  let durationCount = 0;
  listings.forEach((listing) => {
    const duration = calculateDurationDays(
      listing.currentAppearanceStartedAt,
      listing.active ? undefined : listing.lastSeenAt
    );
    totalDurationDays += duration;
    durationCount++;
  });
  const avgListingDurationDays = durationCount > 0 ? Math.round(totalDurationDays / durationCount) : null;

  // Count unique hosts and multi-property hosts
  const hostCounts = new Map<string, number>();
  listings.forEach((listing) => {
    const hostKey = listing.hostId || listing.hostName || `unknown-${listing.id}`;
    hostCounts.set(hostKey, (hostCounts.get(hostKey) || 0) + 1);
  });
  const uniqueHosts = hostCounts.size;
  const multiPropertyHosts = Array.from(hostCounts.values()).filter((count) => count > 1).length;

  // Calculate average price
  const prices = listings
    .map((l) => parsePrice(l.price))
    .filter((p): p is number => p !== null);
  const avgPrice = prices.length > 0
    ? Math.round(prices.reduce((sum, p) => sum + p, 0) / prices.length)
    : null;

  return {
    totalListings: listings.length,
    airbnbListings: airbnbListings.length,
    vrboListings: vrboListings.length,
    activeListings: activeListings.length,
    inactiveListings: inactiveListings.length,
    newListings30d: newListings.length,
    insideBoundary: insideBoundary.length,
    nearbyBoundary: nearbyBoundary.length,
    avgListingDurationDays,
    uniqueHosts,
    multiPropertyHosts,
    avgPrice,
  };
};

/**
 * Group listings by provider, status, and location
 */
export const groupListings = (
  listings: MonitoringListingSummary[]
): ReportListingGroup[] => {
  const groups: ReportListingGroup[] = [];

  // Define all possible groups
  const providerTypes: ("AIRBNB" | "VRBO")[] = ["AIRBNB", "VRBO"];
  const statusTypes: ("Active" | "Inactive")[] = ["Active", "Inactive"];
  const locationTypes: ("Inside Boundary" | "Nearby")[] = ["Inside Boundary", "Nearby"];

  providerTypes.forEach((provider) => {
    statusTypes.forEach((status) => {
      locationTypes.forEach((location) => {
        const filtered = listings.filter((l) => {
          const matchesProvider = l.provider === provider;
          const matchesStatus = status === "Active" ? l.active : !l.active;
          const matchesLocation = location === "Inside Boundary"
            ? l.matchStatus === "INSIDE"
            : l.matchStatus === "NEARBY";
          return matchesProvider && matchesStatus && matchesLocation;
        });

        if (filtered.length > 0) {
          groups.push({
            provider,
            status,
            location,
            listings: filtered,
          });
        }
      });
    });
  });

  return groups;
};

/**
 * Generate report metadata
 */
export const generateMetadata = (
  mode: MonitoringMode,
  community: CommunityBoundary | null,
  addresses: MonitoringAddress[],
  t: TFunction
): ReportMetadata => {
  const now = new Date();
  const jurisdictionName = mode === "COMMUNITY" && community
    ? community.name.split(",")[0]
    : addresses.length > 0
    ? t("report.monitoredAddresses", { count: addresses.length })
    : t("dashboard.monitoringArea");

  return {
    generatedDate: now.toISOString(),
    jurisdictionName,
    mode,
    reportTitle: t("dashboard.reportTitle"),
    reportSubtitle: t("dashboard.reportSubtitle"),
  };
};

/**
 * Transform alert feed data into a structured report format
 */
export const transformToReportData = (
  feed: MonitoringAlertFeed,
  mode: MonitoringMode,
  community: CommunityBoundary | null,
  addresses: MonitoringAddress[],
  t: TFunction
): ReportData => {
  const allListings = extractUniqueListings(feed.alerts);
  const metadata = generateMetadata(mode, community, addresses, t);
  const statistics = calculateStatistics(allListings);
  const groups = groupListings(allListings);

  return {
    metadata,
    statistics,
    groups,
    allListings,
    alerts: feed.alerts,
    latestScan: feed.latestScan,
    community,
    addresses,
  };
};

/**
 * Format listing duration for display
 */
export const formatListingDuration = (
  listing: MonitoringListingSummary,
  t: TFunction
): string =>
  formatLongDuration(
    t,
    listing.currentAppearanceStartedAt,
    listing.active ? undefined : listing.lastSeenAt
  );

/**
 * Format date for display
 */
export const formatReportDate = (dateString: string, locale: AppLocale): string =>
  formatDateTime(dateString, locale);
